import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext.tsx';
import { api } from '../services/api.ts';
import { useConnectivityStatus } from '../hooks/useConnectivityStatus.ts';
import './Profile.css';

const Profile: React.FC = () => {
  const { user, setUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const { isOnline, isBackendOnline } = useConnectivityStatus();
  const backendAvailable = isOnline && isBackendOnline;

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        password: '',
        confirmPassword: ''
      });
    }
    setLoading(false);
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');
    setSaving(true);

    if (!backendAvailable) {
      setMessage('❌ Atualização de perfil exige conexão com o backend');
      setSaving(false);
      return;
    }

    // Validações
    if (!formData.name.trim()) {
      setMessage('❌ O nome é obrigatório');
      setSaving(false);
      return;
    }

    if (!formData.email.trim()) {
      setMessage('❌ O email é obrigatório');
      setSaving(false);
      return;
    }

    if (formData.password && formData.password.length < 6) {
      setMessage('❌ A senha deve ter pelo menos 6 caracteres');
      setSaving(false);
      return;
    }

    if (formData.password && formData.password !== formData.confirmPassword) {
      setMessage('❌ As senhas não coincidem');
      setSaving(false);
      return;
    }

    try {
      const payload: any = {
        name: formData.name.trim(),
        email: formData.email.trim()
      };

      // Só incluir senha se foi preenchida
      if (formData.password) {
        payload.password = formData.password;
      }

      const response = await api.put('/api/v1/auth/profile', payload);
      
      // Atualizar usuário no contexto
      if (response.data) {
        setUser(response.data);
        setMessage('✅ Perfil atualizado com sucesso!');
        
        // Limpar campos de senha
        setFormData(prev => ({
          ...prev,
          password: '',
          confirmPassword: ''
        }));

        // Limpar mensagem após 3 segundos
        setTimeout(() => {
          setMessage('');
        }, 3000);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || 'Erro ao atualizar perfil';
      setMessage(`❌ ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        password: '',
        confirmPassword: ''
      });
    }
    setMessage('');
  };

  if (loading) {
    return (
      <div className="profile">
        <div className="page-header">
          <h1>👤 Perfil</h1>
          <p>Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile">
      <div className="page-header">
        <h1>👤 Meu Perfil</h1>
        <p>Gerencie suas informações pessoais</p>
      </div>

      {!backendAvailable && (
        <div className="message error" style={{ marginBottom: '16px' }}>
          ⚠️ Atualizações de perfil estão indisponíveis sem conexão com o backend.
        </div>
      )}

      <div className="form-container">
        <form onSubmit={handleSubmit} className="profile-form">
          <div className="form-group">
            <label htmlFor="name">Nome</label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="Seu nome completo"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              placeholder="seu@email.com"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Nova Senha (opcional)</label>
            <input
              type="password"
              id="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="Deixe em branco para manter a senha atual"
              minLength={6}
            />
            <small className="form-help">
              Mínimo de 6 caracteres. Deixe em branco se não quiser alterar.
            </small>
          </div>

          {formData.password && (
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirmar Nova Senha</label>
              <input
                type="password"
                id="confirmPassword"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                placeholder="Digite a senha novamente"
                minLength={6}
              />
            </div>
          )}

          {message && (
            <div className={`message ${message.includes('✅') ? 'success' : 'error'}`}>
              {message}
            </div>
          )}

          <div className="form-actions">
            <button 
              type="submit" 
              className="primary-button"
              disabled={saving || !backendAvailable}
            >
              {saving ? 'Salvando...' : 'Salvar Alterações'}
            </button>
            <button 
              type="button" 
              className="secondary-button"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>

      {user && (
        <div className="profile-info">
          <h3>Informações da Conta</h3>
          <div className="info-grid">
            <div className="info-item">
              <strong>ID:</strong>
              <span>{user.id}</span>
            </div>
            <div className="info-item">
              <strong>Admin:</strong>
              <span>{user.is_admin ? '✅ Sim' : '❌ Não'}</span>
            </div>
            {user.created_at && (
              <div className="info-item">
                <strong>Conta criada em:</strong>
                <span>{new Date(user.created_at).toLocaleDateString('pt-BR')}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;

